// Cross-app locale helpers. The cookie pattern lets `emercadohub.com` and
// `vouchergame.emercadohub.com` share a single user-chosen locale without
// any backend lookup — both apps read+write `app_locale` on the parent
// domain. Mirrors the customer-side helper one-to-one so behavior stays
// in lockstep across the two repos.

// `tet` is ISO 639-3 for Tetum, the official language of Timor-Leste.
// ISO 639-1 doesn't list it, so customer + voucher-game both use the
// `tet` short code consistently across the cross-app cookie.
export type AppLocale = 'en' | 'id' | 'tet'

const COOKIE_NAME = 'app_locale'
// Bumped to '3' alongside the customer-side cutover to Tetun default
// (Timor-Leste market). Any client carrying the legacy cookie with a
// mismatched migration value gets force-reset to DEFAULT_LOCALE on
// next visit; keeps both apps yo-yo-free across the rollout.
const MIGRATION_COOKIE_NAME = 'app_locale_migration'
const CURRENT_MIGRATION = '3'
const COOKIE_DOMAIN = '.emercadohub.com'
// Cookie + localStorage stay valid for a year — switching languages is
// rare so a long lifetime keeps Settings sticky across cleared
// localStorage etc.
const COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60

const SUPPORTED: AppLocale[] = ['en', 'id', 'tet']
const DEFAULT_LOCALE: AppLocale = 'tet'

function parseCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const target = `${name}=`
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim()
    if (trimmed.startsWith(target)) {
      return decodeURIComponent(trimmed.slice(target.length))
    }
  }
  return null
}

function isSupported(value: string | null): value is AppLocale {
  return value !== null && (SUPPORTED as string[]).includes(value)
}

/**
 * Resolves the active locale by walking the same chain we serialize to:
 * cookie (cross-domain) → localStorage (same-origin fallback) →
 * DEFAULT_LOCALE. navigator.language is intentionally NOT consulted —
 * the marketplace is Indonesia-first (TL + ID buyers) and most
 * customers' browsers report `en` even when they prefer Bahasa, so
 * trusting the navigator flips the default the wrong way. Customers
 * can always switch via Settings; the cookie persists across visits.
 *
 * One-shot migration: when the migration cookie doesn't match
 * CURRENT_MIGRATION we discard whatever locale the visitor currently
 * carries and reset to DEFAULT_LOCALE, then write the migration
 * cookie so subsequent reads stop overriding the customer's choice.
 * Mirrors the customer-side migration so visitors who hit
 * vouchergame.* first still cut over to id once.
 */
export function readLocale(): AppLocale {
  const migration = parseCookie(MIGRATION_COOKIE_NAME)
  if (migration !== CURRENT_MIGRATION) {
    writeLocale(DEFAULT_LOCALE)
    writeMigration(CURRENT_MIGRATION)
    return DEFAULT_LOCALE
  }

  const cookie = parseCookie(COOKIE_NAME)
  if (isSupported(cookie)) return cookie

  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(COOKIE_NAME) : null
  if (isSupported(stored)) return stored

  return DEFAULT_LOCALE
}

function writeMigration(version: string): void {
  if (typeof document !== 'undefined') {
    const isApex = window.location.hostname.endsWith('emercadohub.com')
    const domainAttr = isApex ? `; Domain=${COOKIE_DOMAIN}` : ''
    document.cookie =
      `${MIGRATION_COOKIE_NAME}=${encodeURIComponent(version)}` +
      `; Path=/${domainAttr}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`
  }
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(MIGRATION_COOKIE_NAME, version)
  }
}

/**
 * Persists the locale choice to every layer that observers might read
 * from. Cookie domain is set to `.emercadohub.com` so the sibling
 * customer app picks up the same value without any URL-param dance.
 *
 * On localhost the cookie domain is intentionally elided — Chrome
 * rejects cookies with explicit Domain attribute on bare hostnames.
 */
export function writeLocale(locale: AppLocale): void {
  if (typeof document !== 'undefined') {
    const isApex = window.location.hostname.endsWith('emercadohub.com')
    const domainAttr = isApex ? `; Domain=${COOKIE_DOMAIN}` : ''
    document.cookie =
      `${COOKIE_NAME}=${encodeURIComponent(locale)}` +
      `; Path=/${domainAttr}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`
  }
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(COOKIE_NAME, locale)
  }
}
